import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaBell } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { listenToProviderBookings } from '../services/bookingService';
import HoneycombLogo from './HoneycombLogo';
import './ServiceProviderHeader.css';

const ServiceProviderHeader = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isNotifOpen, setIsNotifOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const notifRef = useRef(null);
    const profileRef = useRef(null);
    const { currentUser, logout, getProviderProfile } = useAuth();
    const navigate = useNavigate();

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileRef.current && !profileRef.current.contains(event.target)) {
                setIsProfileOpen(false);
            }
            if (notifRef.current && !notifRef.current.contains(event.target)) {
                setIsNotifOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Real-time notifications: pending bookings for this provider
    useEffect(() => {
        if (!currentUser) return;
        let unsubscribe = null;

        const setup = async () => {
            try {
                const profile = await getProviderProfile(currentUser.uid);
                const serviceCategory = profile?.serviceCategory || 'electrician';
                unsubscribe = listenToProviderBookings(
                    currentUser.uid,
                    serviceCategory,
                    (bookings) => {
                        const pending = bookings.filter(b => b.status === 'pending');
                        setNotifications(pending);
                    }
                );
            } catch (err) {
                console.error('Failed to load notifications:', err);
            }
        };

        setup();
        return () => { if (unsubscribe) unsubscribe(); };
    }, [currentUser, getProviderProfile]);

    // Get initials from email for profile avatar
    const getInitials = (email) => {
        if (!email) return 'SP';
        return email.substring(0, 2).toUpperCase();
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Failed to logout:', error);
        }
    };

    return (
        <header className="sp-header">
            <div className="container">
                <nav className="sp-navbar">
                    <Link to={currentUser ? "/dashboard" : "/"} className="sp-logo">
                        <HoneycombLogo size={40} />
                        <span className="logo-text">SkillHive<span className="provider-badge">Partner</span></span>
                    </Link>

                    <button
                        className="menu-toggle"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        aria-label="Toggle menu"
                    >
                        <span></span>
                        <span></span>
                        <span></span>
                    </button>

                    <ul className={`sp-nav-links ${isMenuOpen ? 'active' : ''}`}>
                        <li><Link to="/dashboard">My Work</Link></li>
                        <li><Link to="/job-requests">Job Requests</Link></li>
                        <li><Link to="/earnings">Earnings</Link></li>
                    </ul>

                    <div className="sp-nav-actions">
                        {currentUser ? (
                            <>
                                <div className="notif-wrapper" ref={notifRef}>
                                    <button className="notification-btn" aria-label="Notifications" onClick={() => setIsNotifOpen(!isNotifOpen)}>
                                        <FaBell />
                                        {notifications.length > 0 && (
                                            <span className="notification-badge">{notifications.length}</span>
                                        )}
                                    </button>

                                    {isNotifOpen && (
                                        <div className="notif-dropdown">
                                            <div className="notif-header">
                                                <strong>Notifications</strong>
                                                {notifications.length > 0 && (
                                                    <span className="notif-count">{notifications.length} pending</span>
                                                )}
                                            </div>
                                            {notifications.length === 0 ? (
                                                <div className="notif-empty">No new notifications</div>
                                            ) : (
                                                notifications.map(n => (
                                                    <Link
                                                        key={n.id}
                                                        to="/job-requests"
                                                        className="notif-item"
                                                        onClick={() => setIsNotifOpen(false)}
                                                    >
                                                        <div className="notif-dot" />
                                                        <div className="notif-body">
                                                            <span className="notif-title">{n.serviceCategory || 'Service'} Request</span>
                                                            <span className="notif-sub">{n.customerName || n.customerId || 'Customer'} · {n.createdAt ? new Date(n.createdAt).toLocaleDateString() : ''}</span>
                                                        </div>
                                                    </Link>
                                                ))
                                            )}
                                            <Link to="/job-requests" className="notif-footer" onClick={() => setIsNotifOpen(false)}>
                                                View all job requests →
                                            </Link>
                                        </div>
                                    )}
                                </div>{/* end notif-wrapper */}

                                <div className="profile-dropdown" ref={profileRef}>
                                    <button
                                        className="profile-trigger"
                                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                                        aria-label="Profile menu"
                                    >
                                        <div className="profile-avatar">
                                            {currentUser.photoURL ? (
                                                <img src={currentUser.photoURL} alt="Profile" />
                                            ) : (
                                                <span>{getInitials(currentUser.email)}</span>
                                            )}
                                        </div>
                                    </button>

                                    {isProfileOpen && (
                                        <div className="profile-dropdown-menu">
                                            <div className="dropdown-header">
                                                <div className="dropdown-user-info">
                                                    <strong>{currentUser.displayName || 'Service Partner'}</strong>
                                                    <span className="dropdown-email">{currentUser.email}</span>
                                                </div>
                                            </div>

                                            <div className="dropdown-divider"></div>

                                            <Link
                                                to="/job-requests"
                                                className="dropdown-item"
                                                onClick={() => setIsProfileOpen(false)}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                                    <path d="M3 2h10a1 1 0 011 1v10a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1zm1 2v8h8V4H4z" />
                                                </svg>
                                                Job Requests
                                            </Link>

                                            <Link
                                                to="/earnings"
                                                className="dropdown-item"
                                                onClick={() => setIsProfileOpen(false)}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                                    <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 2a5 5 0 110 10A5 5 0 018 3zm0 2a1 1 0 00-1 1v2a1 1 0 002 0V6a1 1 0 00-1-1z" />
                                                </svg>
                                                Earnings
                                            </Link>

                                            <Link
                                                to="/profile"
                                                className="dropdown-item"
                                                onClick={() => setIsProfileOpen(false)}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                                    <path d="M8 8a3 3 0 100-6 3 3 0 000 6zm0 1c-2.67 0-8 1.34-8 4v1h16v-1c0-2.66-5.33-4-8-4z" />
                                                </svg>
                                                Profile Settings
                                            </Link>

                                            <div className="dropdown-divider"></div>

                                            <button
                                                onClick={handleLogout}
                                                className="dropdown-item logout-item"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                                    <path d="M6 2v2H2v8h4v2H2a2 2 0 01-2-2V4a2 2 0 012-2h4zm8.293 5.293L12 9.586V7a1 1 0 00-2 0v4a1 1 0 001 1h4a1 1 0 000-2h-2.586l2.293-2.293a1 1 0 00-1.414-1.414z" />
                                                </svg>
                                                Logout
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <Link to="/login" className="btn btn-primary">Login</Link>
                        )}
                    </div>
                </nav>
            </div>
        </header>
    );
};

export default ServiceProviderHeader;
